import {
  csrfHeaders,
  getAccessToken,
  getRefreshToken,
  hasAuthSession,
  isTokenExpired,
  logout,
  refreshAccessToken,
} from "./auth";

const API_URL = process.env.REACT_APP_API_URL;

if (!API_URL) {
  throw new Error(
    "REACT_APP_API_URL is undefined. Check Azure Static Web App build configuration."
  );
}

function getErrorMessage(errorData) {
  if (!errorData) {
    return "API request failed";
  }

  if (typeof errorData === "string") {
    return errorData;
  }

  if (errorData.detail) {
    return errorData.detail;
  }

  if (Array.isArray(errorData.non_field_errors) && errorData.non_field_errors.length) {
    return errorData.non_field_errors[0];
  }

  const keys = Object.keys(errorData);
  if (!keys.length) {
    return "API request failed";
  }

  const firstValue = errorData[keys[0]];
  if (Array.isArray(firstValue) && firstValue.length) {
    return firstValue[0];
  }

  if (typeof firstValue === "string") {
    return firstValue;
  }

  return "API request failed";
}

export async function apiFetch(endpoint, options = {}) {
  let token = getAccessToken();
  let attemptedRefresh = false;
  const sessionAvailable = hasAuthSession();

  if (token && isTokenExpired(token) && getRefreshToken()) {
    try {
      token = await refreshAccessToken();
      attemptedRefresh = true;
    } catch (error) {
      logout({ remote: false });
      throw new Error("Session expired");
    }
  }

  if (!token && getRefreshToken()) {
    try {
      token = await refreshAccessToken();
      attemptedRefresh = true;
    } catch (error) {
      logout({ remote: false });
      throw new Error("Session expired");
    }
  }

  if (!token && !sessionAvailable) {
    logout({ remote: false });
    throw new Error("Not authenticated");
  }

  const method = String(options.method || "GET").toUpperCase();
  const unsafeHeaders = method === "GET" || method === "HEAD" ? {} : csrfHeaders();
  const makeRequest = (accessToken) =>
    fetch(`${API_URL}${endpoint}`, {
      ...options,
      credentials: "include",
      headers: {
        ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
        ...unsafeHeaders,
        ...(options.headers || {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

  let response = await makeRequest(token);

  // Handle auth failure centrally
  if (response.status === 401) {
    if (!attemptedRefresh && hasAuthSession()) {
      try {
        const refreshedToken = await refreshAccessToken();
        response = await makeRequest(refreshedToken || getAccessToken());
      } catch (error) {
        logout({ remote: false });
        throw new Error("Session expired");
      }
    } else {
      logout({ remote: false });
      throw new Error("Session expired");
    }
  }

  // Handle non-OK responses
  if (!response.ok) {
    let errorData = null;
    try {
      errorData = await response.json();
    } catch {
      // ignore JSON parse errors
    }

    const error = new Error(getErrorMessage(errorData));
    error.status = response.status;
    error.errors = errorData;
    throw error;
  }

  // No content response (204)
  if (response.status === 204) {
    return null;
  }

  const data = await response.json();
  if (
    data &&
    typeof data === "object" &&
    Array.isArray(data.results) &&
    Object.prototype.hasOwnProperty.call(data, "count")
  ) {
    return data.results;
  }
  return data;
}
